import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import html2pdf from 'html2pdf.js';
import { getApi } from '../../../Services/apiService';
import { CheckrReportView } from '../../../LandlordComponent/PropertiesComponent/TenantDetail/BackgroundCheckReport';

export const MY_REPORT_URL = '/verification/tenant/checkr/my-report';

// The tenant's own Checkr report: for ?property_id=..., or their latest check
const MyBackgroundCheckReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('property_id') || '';
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const response = await getApi(MY_REPORT_URL, propertyId ? { params: { property_id: propertyId } } : {});
        if (cancelled) return;
        if (response?.statusCode === 200) {
          setReport(response?.data ?? null);
        } else {
          toast.error(response?.message || 'Failed to fetch background check report.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching Checkr report:', error);
        toast.error(error?.response?.data?.message || 'Failed to fetch background check report. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(propertyId ? `/tenant/properties/${propertyId}` : '/tenant/settings/background-check');
  };

  // No tenant PDF endpoint: render the on-screen report to a PDF file in the browser
  const handleDownload = async () => {
    const element = document.querySelector('.checkr-print-area');
    if (!element) return;
    setIsDownloading(true);
    try {
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: 'background-check-report.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          // The export class hides the buttons and expands every section (see App.css)
          html2canvas: {
            scale: 2,
            useCORS: true,
            onclone: (doc) => doc.querySelector('.checkr-print-area')?.classList.add('checkr-pdf-export'),
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'], avoid: ['.checkr-hero', '.checkr-section-toggle', '.checkr-score', '.checkr-stat', '.checkr-group-header', '.checkr-record'] },
        })
        .from(element)
        .save();
    } catch (error) {
      console.error('Error generating Checkr report PDF:', error);
      toast.error('Failed to download the report PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <CheckrReportView
      report={report}
      isLoading={isLoading}
      onBack={goBack}
      onDownload={handleDownload}
      isDownloading={isDownloading}
      // report is null until the check is completed
      emptyMessage="Your background check report is not ready yet."
    />
  );
};

export default MyBackgroundCheckReport;
